/**
 * Schedulers Page
 * 
 * List and manage recurring payment schedulers.
 * Premium feature only.
 * 
 * @validates Requirements 9.1-9.8 - Scheduler management
 */

import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useSchedulerStore, getPeriodicityLabel } from '../store/schedulerStore';
import { useAccountStore } from '../store/accountStore';
import { useUserSettingsStore, getDefaultAccountId } from '../store/userSettingsStore';
import { Card, Button, Modal } from '../components/ui';
import { AccountSelector } from '../components/ui/AccountSelector';
import { formatCurrency, formatDate } from '../utils/formatters';
import type { Scheduler } from '@budgira/shared';

const SchedulersPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { accountId: urlAccountId } = useParams<{ accountId: string }>();
  const { selectedAccount, selectAccount, accounts, fetchAccounts, fetchBalance } = useAccountStore();
  const { preferences } = useUserSettingsStore();
  const {
    schedulers,
    isLoading,
    statusFilter,
    page,
    pageSize,
    fetchSchedulers,
    deleteScheduler,
    terminateScheduler,
    generateOperations,
    setStatusFilter,
    setPage,
    setPageSize,
  } = useSchedulerStore();

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [terminateModalOpen, setTerminateModalOpen] = useState(false);
  const [generateModalOpen, setGenerateModalOpen] = useState(false);
  const [selectedScheduler, setSelectedScheduler] = useState<Scheduler | null>(null);
  const [generateDate, setGenerateDate] = useState(new Date().toISOString().split('T')[0]);
  const [generateResult, setGenerateResult] = useState<number | null>(null);

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{
    scheduler: Scheduler;
    x: number;
    y: number;
  } | null>(null);
  const contextMenuRef = useRef<HTMLDivElement>(null);
  const longPressTimer = useRef<NodeJS.Timeout | null>(null);
  const longPressTriggered = useRef(false);

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(event.target as Node)) {
        setContextMenu(null);
      }
    };
    const handleScroll = () => setContextMenu(null);
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setContextMenu(null);
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('scroll', handleScroll, true);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('scroll', handleScroll, true);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const accountId = urlAccountId || selectedAccount?.id;

  useEffect(() => {
    if (accounts.length === 0) {
      fetchAccounts();
    }
  }, [accounts.length, fetchAccounts]);

  // Select default account if none selected and no URL param
  useEffect(() => {
    if (!urlAccountId && !selectedAccount && accounts.length > 0) {
      const defaultAccountId = getDefaultAccountId();
      if (defaultAccountId && accounts.some(a => a.id === defaultAccountId)) {
        selectAccount(defaultAccountId);
      } else if (accounts[0]) {
        selectAccount(accounts[0].id);
      }
    }
  }, [urlAccountId, selectedAccount, accounts, selectAccount]);

  useEffect(() => {
    if (urlAccountId) {
      selectAccount(urlAccountId);
    }
  }, [urlAccountId, selectAccount]);

  // Sync pageSize with user preferences
  useEffect(() => {
    if (preferences?.schedulersPageSize && preferences.schedulersPageSize !== pageSize) {
      setPageSize(preferences.schedulersPageSize);
    }
  }, [preferences?.schedulersPageSize, pageSize, setPageSize]);

  useEffect(() => {
    if (accountId) {
      fetchSchedulers(accountId);
    }
  }, [accountId, statusFilter, fetchSchedulers]);

  // Calculate pagination (client-side)
  const totalSchedulers = schedulers.length;
  const totalPages = Math.max(1, Math.ceil(totalSchedulers / pageSize));
  const startIndex = (page - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalSchedulers);
  const paginatedSchedulers = schedulers.slice(startIndex, endIndex);

  // Calculate total balance of active schedulers (credits - debits)
  const activeSchedulers = schedulers.filter(s => s.status === 'active');
  const schedulersTotal = activeSchedulers.reduce((acc, scheduler) => {
    const amount = scheduler.direction === 'credit' ? scheduler.amount : -scheduler.amount;
    return acc + amount;
  }, 0);

  // Reset to page 1 if current page is beyond total pages
  useEffect(() => {
    if (page > totalPages && totalPages > 0) {
      setPage(1);
    }
  }, [page, totalPages, setPage]);

  const handleAccountChange = (newAccountId: string) => {
    navigate(`/accounts/${newAccountId}/schedulers`);
  };

  const handleDeleteClick = (scheduler: Scheduler) => {
    setSelectedScheduler(scheduler);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (selectedScheduler) {
      await deleteScheduler(selectedScheduler.id);
      setDeleteModalOpen(false);
      setSelectedScheduler(null);
      if (accountId) {
        fetchSchedulers(accountId);
      }
    }
  };

  const handleTerminateClick = (scheduler: Scheduler) => {
    setSelectedScheduler(scheduler);
    setTerminateModalOpen(true);
  };

  const handleTerminateConfirm = async () => {
    if (selectedScheduler) {
      await terminateScheduler(selectedScheduler.id);
      setTerminateModalOpen(false);
      setSelectedScheduler(null);
      if (accountId) {
        fetchSchedulers(accountId);
      }
    }
  };

  // Context menu handlers
  const handleContextMenu = (e: React.MouseEvent, scheduler: Scheduler) => {
    e.preventDefault();
    // Calculate position to keep menu in viewport
    const x = Math.min(e.clientX, window.innerWidth - 200);
    const y = Math.min(e.clientY, window.innerHeight - 250);
    setContextMenu({ scheduler, x, y });
  };

  const handleTouchStart = (scheduler: Scheduler, e: React.TouchEvent) => {
    longPressTriggered.current = false;
    const touch = e.touches[0];
    if (!touch) return;
    longPressTimer.current = setTimeout(() => {
      longPressTriggered.current = true;
      const x = Math.min(touch.clientX, window.innerWidth - 200);
      const y = Math.min(touch.clientY, window.innerHeight - 250);
      setContextMenu({ scheduler, x, y });
    }, 500);
  };

  const handleTouchEnd = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const handleTouchMove = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const handleContextMenuEdit = () => {
    if (contextMenu) {
      navigate(`/schedulers/${contextMenu.scheduler.id}/edit`);
      setContextMenu(null);
    }
  };

  const handleContextMenuTerminate = () => {
    if (contextMenu) {
      handleTerminateClick(contextMenu.scheduler);
      setContextMenu(null);
    }
  };

  const handleContextMenuDuplicate = () => {
    if (contextMenu) {
      // Navigate to new scheduler form with pre-filled data
      navigate(`/accounts/${accountId}/schedulers/new`, { 
        state: { duplicateFrom: contextMenu.scheduler } 
      });
      setContextMenu(null);
    }
  };

  const handleContextMenuDelete = () => {
    if (contextMenu) {
      handleDeleteClick(contextMenu.scheduler);
      setContextMenu(null);
    }
  };

  const handleGenerateClick = () => {
    setGenerateDate(new Date().toISOString().split('T')[0]);
    setGenerateResult(null);
    setGenerateModalOpen(true);
  };

  const handleGenerateConfirm = async () => {
    if (accountId && generateDate) {
      const count = await generateOperations(accountId, generateDate);
      setGenerateResult(count);
      if (accountId) {
        fetchSchedulers(accountId);
        // Explicitly refresh balance after generating operations
        fetchBalance(accountId);
      }
    }
  };

  if (!accountId || !selectedAccount) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">{t('schedulers.title')}</h1>
        <Card>
          <div className="text-center py-8">
            <p className="text-slate-500 dark:text-slate-400">{t('operations.selectAccount')}</p>
            <Link to="/accounts" className="mt-4 inline-block">
              <Button>{t('operations.goToAccounts')}</Button>
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  // Check if user can modify schedulers on this account
  const canWrite = !selectedAccount.isShared || selectedAccount.permission === 'write' || selectedAccount.permission === 'owner';

  return (
    <div className="space-y-6">
      {/* Header with Account Selector */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <AccountSelector
            accounts={accounts}
            selectedAccountId={accountId}
            onAccountChange={handleAccountChange}
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'terminated')}
            className="px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg text-sm text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-violet-500/20"
          >
            <option value="all">{t('schedulers.filter.all')}</option>
            <option value="active">{t('schedulers.filter.active')}</option>
            <option value="terminated">{t('schedulers.filter.terminated')}</option>
          </select>
          {canWrite && (
            <>
              <Button variant="secondary" onClick={handleGenerateClick}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span className="hidden sm:inline ml-1">{t('schedulers.generate')}</span>
              </Button>
              <Link to={`/accounts/${accountId}/schedulers/new`}>
                <Button>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  <span className="hidden sm:inline ml-1">{t('schedulers.add')}</span>
                </Button>
              </Link>
            </>
          )}
          <Link to={`/accounts/${accountId}/schedulers/export`}>
            <Button variant="outline" title={t('schedulerExport.title')}>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              <span className="hidden sm:inline ml-1">{t('common.export')}</span>
            </Button>
          </Link>
        </div>
      </div>

      {/* Total balance display - stacked on mobile, side by side on desktop */}
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
        <div className="flex-1 bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 py-2 sm:py-4 px-4 sm:px-6">
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">{t('schedulers.totalBalance')}</p>
          <p className={`text-lg sm:text-xl font-bold ${schedulersTotal >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
            {formatCurrency(Math.abs(schedulersTotal), selectedAccount?.currency || 'EUR')}
            <span className="text-xs font-normal ml-1">({t('schedulers.perPeriod')})</span>
          </p>
        </div>
        <div className="flex-1 bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 py-2 sm:py-4 px-4 sm:px-6">
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">{t('schedulers.activeCount')} : <span className="text-lg sm:text-xl font-bold text-slate-700 dark:text-slate-200">{activeSchedulers.length}</span></p>
        </div>
      </div>

      {/* Schedulers Table */}
      <Card>
        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-16 bg-slate-100 rounded animate-pulse" />
            ))}
          </div>
        ) : schedulers.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto mb-4 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">{t('schedulers.empty.title')}</h3>
            <p className="text-slate-500 dark:text-slate-400 mb-4">{t('schedulers.empty.message')}</p>
            {canWrite && (
              <Link to={`/accounts/${accountId}/schedulers/new`}>
                <Button>{t('schedulers.empty.create')}</Button>
              </Link>
            )}
          </div>
        ) : (
          <div className="overflow-hidden">
            {/* Mobile card view */}
            <div className="sm:hidden">
              {/* Mobile column headers */}
              <div className="flex items-center justify-between px-1 py-2 border-b border-slate-200 dark:border-slate-700 text-xs font-medium text-slate-500 dark:text-slate-400">
                <span>{t('schedulers.mobileHeader.description', 'Tiers')}</span>
                <span>{t('schedulers.mobileHeader.amount', 'Montant')}</span>
              </div>
              <div className="divide-y divide-slate-100 dark:divide-slate-700">
                {paginatedSchedulers.map((scheduler) => (
                  <MobileSchedulerCard
                    key={scheduler.id}
                    scheduler={scheduler}
                    currency={selectedAccount.currency}
                    canWrite={canWrite}
                    onDelete={() => handleDeleteClick(scheduler)}
                    onTerminate={() => handleTerminateClick(scheduler)}
                    onTouchStart={handleTouchStart}
                    onTouchEnd={handleTouchEnd}
                    onTouchMove={handleTouchMove}
                    t={t}
                  />
                ))}
              </div>
            </div>

            {/* Desktop table */}
            <table className="hidden sm:table w-full">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800">
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600 dark:text-slate-200">{t('schedulers.table.description')}</th>
                  <th className="hidden md:table-cell text-left py-3 px-4 text-sm font-medium text-slate-600 dark:text-slate-200">{t('schedulers.table.periodicity')}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600 dark:text-slate-200">{t('schedulers.table.nextOccurrence')}</th>
                  <th className="hidden lg:table-cell text-left py-3 px-4 text-sm font-medium text-slate-600 dark:text-slate-200">{t('schedulers.table.category')}</th>
                  <th className="hidden xl:table-cell text-left py-3 px-4 text-sm font-medium text-slate-600 dark:text-slate-200">{t('schedulers.table.paymentDetails')}</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-slate-600 dark:text-slate-200">{t('schedulers.table.amount')}</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-slate-600 dark:text-slate-200">{t('schedulers.table.status')}</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-slate-600 dark:text-slate-200 w-28">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {paginatedSchedulers.map((scheduler) => (
                  <SchedulerRow
                    key={scheduler.id}
                    scheduler={scheduler}
                    currency={selectedAccount.currency}
                    canWrite={canWrite}
                    onDelete={() => handleDeleteClick(scheduler)}
                    onTerminate={() => handleTerminateClick(scheduler)}
                    onContextMenu={handleContextMenu}
                    onTouchStart={handleTouchStart}
                    onTouchEnd={handleTouchEnd}
                    onTouchMove={handleTouchMove}
                    t={t}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex flex-wrap items-center justify-center gap-1 sm:gap-2 pt-4 border-t border-slate-200 dark:border-slate-700 mt-4">
            {/* First page button - always visible, disabled when on first page */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(1)}
              disabled={page === 1}
              title={t('common.first')}
              className="px-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
              </svg>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              className="px-2 sm:px-3"
            >
              <span className="hidden sm:inline">{t('common.previous')}</span>
              <svg className="w-4 h-4 sm:hidden" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </Button>
            <span className="text-sm text-slate-500 whitespace-nowrap px-1">
              {page} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page + 1)}
              disabled={page === totalPages}
              className="px-2 sm:px-3"
            >
              <span className="hidden sm:inline">{t('common.next')}</span>
              <svg className="w-4 h-4 sm:hidden" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Button>
            {/* Last page button - always visible, disabled when on last page */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(totalPages)}
              disabled={page === totalPages}
              title={t('common.last')}
              className="px-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
              </svg>
            </Button>
          </div>
        )}
      </Card>

      {/* Context Menu */}
      {contextMenu && (
        <div
          ref={contextMenuRef}
          className="fixed z-50 bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-slate-200 dark:border-slate-600 py-1 min-w-[180px] animate-in fade-in zoom-in-95 duration-100"
          style={{ 
            left: contextMenu.x, 
            top: contextMenu.y,
          }}
        >
          {/* Edit */}
          <button
            onClick={handleContextMenuEdit}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
          >
            <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            {t('common.edit')}
          </button>

          {/* Terminate - only for active schedulers */}
          {contextMenu.scheduler.status === 'active' && canWrite && (
            <button
              onClick={handleContextMenuTerminate}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
            >
              <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
              </svg>
              {t('schedulers.terminate')}
            </button>
          )}

          {/* Duplicate - only if user can write */}
          {canWrite && (
            <button
              onClick={handleContextMenuDuplicate}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
            >
              <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              {t('schedulers.actions.duplicate')}
            </button>
          )}

          {/* Separator */}
          {canWrite && <div className="border-t border-slate-100 dark:border-slate-600 my-1" />}

          {/* Delete - only if user can write */}
          {canWrite && (
            <button
              onClick={handleContextMenuDelete}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              {t('common.delete')}
            </button>
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title={t('schedulers.deleteModal.title')}
        footer={
          <>
            <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {t('common.delete')}
            </Button>
          </>
        }
      >
        <p className="text-slate-600 dark:text-slate-300">{t('schedulers.deleteModal.message')}</p>
      </Modal>

      {/* Terminate Confirmation Modal */}
      <Modal
        isOpen={terminateModalOpen}
        onClose={() => setTerminateModalOpen(false)}
        title={t('schedulers.terminateModal.title')}
        footer={
          <>
            <Button variant="outline" onClick={() => setTerminateModalOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={handleTerminateConfirm}
              className="bg-orange-600 hover:bg-orange-700 text-white"
            >
              {t('schedulers.terminateModal.confirm')}
            </Button>
          </>
        }
      >
        <p className="text-slate-600 dark:text-slate-300">{t('schedulers.terminateModal.message')}</p>
      </Modal>

      {/* Generate Operations Modal */}
      <Modal
        isOpen={generateModalOpen}
        onClose={() => setGenerateModalOpen(false)}
        title={t('schedulers.generateModal.title')}
        footer={
          generateResult === null ? (
            <>
              <Button variant="outline" onClick={() => setGenerateModalOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button onClick={handleGenerateConfirm}>
                {t('schedulers.generateModal.confirm')}
              </Button>
            </>
          ) : (
            <Button onClick={() => setGenerateModalOpen(false)}>
              {t('common.close')}
            </Button>
          )
        }
      >
        {generateResult === null ? (
          <div className="space-y-4">
            <p className="text-slate-600 dark:text-slate-300">{t('schedulers.generateModal.message')}</p>
            <div>
              <label htmlFor="generateDate" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                {t('schedulers.generateModal.dateLabel')}
              </label>
              <input
                type="date"
                id="generateDate"
                value={generateDate}
                onChange={(e) => setGenerateDate(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500"
              />
            </div>
          </div>
        ) : (
          <div className="text-center py-4">
            <div className="w-16 h-16 mx-auto mb-4 bg-green-100 dark:bg-green-900/40 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-lg font-medium text-slate-900 dark:text-slate-100">
              {t('schedulers.generateModal.result', { count: generateResult })}
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
};

/**
 * Mobile Scheduler Card Component
 */
interface MobileSchedulerCardProps {
  scheduler: Scheduler;
  currency: string;
  canWrite: boolean;
  onDelete: () => void;
  onTerminate: () => void;
  onTouchStart: (scheduler: Scheduler, e: React.TouchEvent) => void;
  onTouchEnd: () => void;
  onTouchMove: () => void;
  t: (key: string) => string;
}

const MobileSchedulerCard = ({
  scheduler,
  currency,
  canWrite,
  onDelete,
  onTouchStart,
  onTouchEnd,
  onTouchMove,
  t
}: MobileSchedulerCardProps) => {
  const isTerminated = scheduler.status === 'terminated';

  return (
    <div
      className={`py-3 px-2 ${isTerminated ? 'opacity-60' : ''}`}
      onTouchStart={(e) => onTouchStart(scheduler, e)}
      onTouchEnd={onTouchEnd}
      onTouchMove={onTouchMove}
    >
      <div className="flex items-start justify-between gap-2">
        {/* Left: Info */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
            {scheduler.counterparty?.name || '—'}
          </p>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {formatDate(scheduler.nextOccurrence)} · {getPeriodicityLabel(scheduler.periodicity, t)}
          </p>
          {scheduler.categories && scheduler.categories.length > 0 && scheduler.categories[0]?.category?.name && (
            <span className="inline-flex items-center mt-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300">
              {scheduler.categories[0].category.logoUrl && (
                <span className="mr-1">{scheduler.categories[0].category.logoUrl}</span>
              )}
              {scheduler.categories[0].category.name}
            </span>
          )}
        </div>
        
        {/* Right: Amount + Status + Actions */}
        <div className="flex items-center gap-2">
          <div className="text-right">
            <span className={`text-sm font-medium whitespace-nowrap ${scheduler.direction === 'credit' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
              {scheduler.direction === 'credit' ? '+' : '-'}
              {formatCurrency(scheduler.amount, currency)}
            </span>
            <p className={`text-xs ${isTerminated ? 'text-slate-500 dark:text-slate-400' : 'text-green-600 dark:text-green-400'}`}>
              {isTerminated ? t('schedulers.status.terminated') : t('schedulers.status.active')}
            </p>
          </div>
          
          {/* Actions */}
          {canWrite && (
            <div className="flex flex-col gap-0.5">
              <Link
                to={`/schedulers/${scheduler.id}/edit`}
                className="p-1.5 text-slate-400 hover:text-violet-600 dark:hover:text-violet-400 rounded"
                title={t('common.edit')}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </Link>
              <button
                onClick={onDelete}
                className="p-1.5 text-slate-400 hover:text-red-600 dark:hover:text-red-400 rounded"
                title={t('common.delete')}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Scheduler Row Component
 */
interface SchedulerRowProps {
  scheduler: Scheduler;
  currency: string;
  canWrite: boolean;
  onDelete: () => void;
  onTerminate: () => void;
  onContextMenu: (e: React.MouseEvent, scheduler: Scheduler) => void;
  onTouchStart: (scheduler: Scheduler, e: React.TouchEvent) => void;
  onTouchEnd: () => void;
  onTouchMove: () => void;
  t: (key: string) => string;
}

const SchedulerRow = ({ 
  scheduler, 
  currency, 
  canWrite, 
  onDelete, 
  onTerminate,
  onContextMenu,
  onTouchStart,
  onTouchEnd,
  onTouchMove,
  t 
}: SchedulerRowProps) => {
  const isTerminated = scheduler.status === 'terminated';

  return (
    <tr 
      className={`border-b border-slate-100 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/50 ${isTerminated ? 'opacity-60' : ''} cursor-context-menu`}
      onContextMenu={(e) => onContextMenu(e, scheduler)}
      onTouchStart={(e) => onTouchStart(scheduler, e)}
      onTouchEnd={onTouchEnd}
      onTouchMove={onTouchMove}
    >
      <td className="py-3 px-4">
        <div className="min-w-0">
          <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
            {scheduler.counterparty?.name || '—'}
          </p>
          {scheduler.comment && (
            <p className="text-xs text-slate-400 dark:text-slate-500 truncate hidden sm:block">{scheduler.comment}</p>
          )}
          {/* Show periodicity on mobile */}
          <span className="md:hidden text-xs text-slate-500 dark:text-slate-400">
            {getPeriodicityLabel(scheduler.periodicity, t)}
          </span>
        </div>
      </td>
      <td className="hidden md:table-cell py-3 px-4">
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-violet-50 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300">
          {getPeriodicityLabel(scheduler.periodicity, t)}
        </span>
      </td>
      <td className="hidden sm:table-cell py-3 px-4">
        <span className="text-sm text-slate-900 dark:text-slate-100">{formatDate(scheduler.nextOccurrence)}</span>
        {scheduler.endDate && (
          <p className="text-xs text-slate-400 dark:text-slate-500">
            {t('schedulers.until')} {formatDate(scheduler.endDate)}
          </p>
        )}
      </td>
      <td className="hidden lg:table-cell py-3 px-4">
        {scheduler.categories && scheduler.categories.length > 0 && scheduler.categories[0]?.category?.name ? (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300">
            {scheduler.categories[0].category.logoUrl && (
              <span className="mr-1">{scheduler.categories[0].category.logoUrl}</span>
            )}
            {scheduler.categories[0].category.name}
          </span>
        ) : (
          <span className="text-slate-400 dark:text-slate-500">—</span>
        )}
      </td>
      <td className="hidden xl:table-cell py-3 px-4">
        <span className="text-sm text-slate-600 dark:text-slate-400">{scheduler.paymentDetails || '—'}</span>
      </td>
      <td className="py-3 px-4 text-right">
        <span className={`text-sm font-medium whitespace-nowrap ${scheduler.direction === 'credit' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
          {scheduler.direction === 'credit' ? '+' : '-'}
          {formatCurrency(scheduler.amount, currency)}
        </span>
      </td>
      <td className="py-3 px-4 text-center">
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
          isTerminated
            ? 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400'
            : 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400'
        }`}>
          {isTerminated ? t('schedulers.status.terminated') : t('schedulers.status.active')}
        </span>
      </td>
      <td className="py-3 px-4 text-right">
        {canWrite ? (
          <div className="flex items-center justify-end gap-1">
            {!isTerminated && (
              <button
                onClick={onTerminate}
                className="p-2 text-slate-500 dark:text-slate-400 hover:text-orange-600 dark:hover:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/30 rounded-lg transition-colors"
                title={t('schedulers.terminate')}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                </svg>
              </button>
            )}
            <Link
              to={`/schedulers/${scheduler.id}/edit`}
              className="p-2 text-slate-500 dark:text-slate-400 hover:text-violet-600 dark:hover:text-violet-400 hover:bg-violet-50 dark:hover:bg-violet-900/30 rounded-lg transition-colors"
              title={t('common.edit')}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </Link>
            <button
              onClick={onDelete}
              className="p-2 text-slate-500 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
              title={t('common.delete')}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        ) : (
          <span className="text-xs text-slate-400 dark:text-slate-500 italic">{t('operations.readOnly')}</span>
        )}
      </td>
    </tr>
  );
};

export default SchedulersPage;
